// src/components/task/TaskDetailsModal.tsx
import React, { useState, useEffect } from 'react';
import {
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  collection,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy,
  where,
  getDocs,
  type Timestamp
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Task, TaskNote } from '../../types/tasks';
import { format, parseISO, isValid } from 'date-fns';
import { X, Edit2, Trash2, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSchool } from '../../contexts/SchoolContext';

interface TaskDetailsModalProps {
  task: Task;
  onClose: () => void;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
}

export default function TaskDetailsModal({
  task,
  onClose
}: TaskDetailsModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState(task);
  const [newNote, setNewNote] = useState('');
  const [currentStatus, setCurrentStatus] = useState<Task['status']>(task.status);
  const [notes, setNotes] = useState<TaskNote[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [noteAuthors, setNoteAuthors] = useState<Record<string, string>>({});
  const { selectedSchool } = useSchool();

  const user = auth.currentUser;
  const canEdit = user?.uid === task.createdBy;
  const isAssigned = user && task.assignedTo?.includes(user.uid);
  const canAddNotes = canEdit || isAssigned; // Allow task creator or assigned users to add notes

  // Subscribe to notes subcollection
  useEffect(() => {
    const notesCol = collection(db, 'tasks', task.id, 'notes');
    const notesQuery = query(notesCol, orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      notesQuery,
      async (snap) => {
        const loaded = snap.docs.map(docSnap => ({
          id: docSnap.id,
          ...(docSnap.data() as Omit<TaskNote, 'id'>)
        }));
        setNotes(loaded);
        
        // Load authors for all notes
        const authorIds = [...new Set(loaded.map(note => note.createdBy))];
        const authors: Record<string, string> = {};
        
        for (const authorId of authorIds) {
          try {
            const userDoc = await getDocs(
              query(
                collection(db, 'users'),
                where('__name__', '==', authorId)
              )
            );
            
            if (!userDoc.empty) {
              const userData = userDoc.docs[0].data();
              authors[authorId] = userData.fullName || userData.displayName || userData.email?.split('@')[0] || 'Unknown';
            } else {
              authors[authorId] = 'Unknown';
            }
          } catch (error) {
            console.error('Error loading note author:', error);
            authors[authorId] = 'Unknown';
          }
        }
        
        setNoteAuthors(authors);
      },
      (err) => {
        console.error('Notes subscription error:', err);
      }
    );
    return () => unsub();
  }, [task.id]);

  // Load school members when editing
  useEffect(() => {
    const loadSchoolMembers = async () => {
      if (!isEditing || !selectedSchool) return;

      try {
        const membersQuery = query(
          collection(db, 'school_members'),
          where('schoolId', '==', selectedSchool.id)
        );
        const memberSnapshot = await getDocs(membersQuery);
        
        const userPromises = memberSnapshot.docs.map(async (memberDoc) => {
          const userDoc = await getDocs(
            query(
              collection(db, 'users'),
              where('__name__', '==', memberDoc.data().userId)
            )
          );
          
          if (!userDoc.empty) {
            const userData = userDoc.docs[0].data();
            return {
              id: memberDoc.data().userId,
              name: userData.fullName || userData.displayName || userData.email?.split('@')[0] || 'Unknown User',
              email: userData.email || ''
            };
          }
          return null;
        });

        const users = (await Promise.all(userPromises)).filter((user): user is UserOption => user !== null);
        setUsers(users);
      } catch (error) {
        console.error('Error loading school members:', error);
      }
    };

    loadSchoolMembers();
  }, [isEditing, selectedSchool]);

  // Normalize Date or Timestamp into a JS Date
  const formatDate = (input: string | Timestamp | null) => {
    if (!input) {
      return 'Just now';
    }
    
    let date: Date;
    if (typeof input === 'object' && input !== null && 'toDate' in input && typeof input.toDate === 'function') {
      date = input.toDate();
    } else {
      date = parseISO(String(input));
    }
    return isValid(date) ? format(date, 'PPp') : 'Invalid date';
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await updateDoc(doc(db, 'tasks', task.id), {
        ...formData,
        updatedAt: serverTimestamp()
      });
      toast.success('Task updated');
      onClose();
    } catch (err) {
      console.error('Error updating task:', err);
      toast.error('Failed to update task');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'tasks', task.id));
      toast.success('Task deleted');
      onClose();
    } catch (err) {
      console.error('Error deleting task:', err);
      toast.error('Failed to delete task');
    }
  };

  const handleStatusChange = async (status: Task['status']) => {
    try {
      setLoading(true);
      await updateDoc(doc(db, 'tasks', task.id), {
        status,
        updatedAt: serverTimestamp()
      });
      setCurrentStatus(status);
      setFormData(prev => ({ ...prev, status }));
      toast.success('Status updated');
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !user) return;
    try {
      setLoading(true);
      const notesCol = collection(db, 'tasks', task.id, 'notes');
      await addDoc(notesCol, {
        content: newNote.trim(),
        createdBy: user.uid,
        createdAt: serverTimestamp()
      });
      setNewNote('');
      toast.success('Note added');
    } catch (err) {
      console.error('Error adding note:', err);
      toast.error('Failed to add note');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-light rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-light">
          <h2 className="text-xl font-semibold text-text-primary">
            {isEditing ? 'Edit Task' : 'Task Details'}
          </h2>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-text-secondary mb-1">
                  Title
                </label>
                <input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-text-secondary mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  rows={3}
                />
              </div>

              <div>
                <label htmlFor="dueDate" className="block text-sm font-medium text-text-secondary mb-1">
                  Due Date
                </label>
                <input
                  id="dueDate"
                  type="datetime-local"
                  value={formData.dueDate || ''}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                />
              </div>

              <div>
                <label htmlFor="priority" className="block text-sm font-medium text-text-secondary mb-1">
                  Priority
                </label>
                <select
                  id="priority"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as Task['priority'] })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div>
                <label htmlFor="assignedTo" className="block text-sm font-medium text-text-secondary mb-1">
                  Assign To
                </label>
                <select
                  id="assignedTo"
                  multiple
                  value={formData.assignedTo || []}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    assignedTo: Array.from(e.target.selectedOptions, option => option.value) 
                  })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  size={4}
                >
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-text-secondary mt-1">
                  Hold Ctrl (or Cmd) to select multiple users
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Title & Description */}
              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-2">
                  {task.title}
                </h3>
                {task.description && (
                  <p className="text-text-secondary whitespace-pre-wrap">
                    {task.description}
                  </p>
                )}
              </div>

              {/* Status Controls */}
              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Status</h4>
                <div className="flex gap-2">
                  {(['pending', 'in-progress', 'completed'] as Task['status'][]).map(s => (
                    <button
                      key={s}
                      onClick={() => handleStatusChange(s)}
                      disabled={loading}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        currentStatus === s
                          ? 'bg-primary-500 text-white'
                          : 'bg-background text-text-secondary hover:text-primary-500'
                      }`}
                    >
                      {s.replace('-', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Notes</h4>
                <div className="space-y-4 mb-4">
                  {notes.length === 0 && (
                    <p className="text-text-secondary">No notes yet</p>
                  )}
                  {notes.map(note => (
                    <div key={note.id} className="bg-background rounded-lg p-4">
                      <p className="text-text-primary whitespace-pre-wrap">
                        {note.content}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <p className="text-xs text-text-muted">
                          {noteAuthors[note.createdBy] || 'Unknown'}
                        </p>
                        <span className="text-xs text-text-muted">•</span>
                        <p className="text-xs text-text-muted">
                          {formatDate(note.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {canAddNotes && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newNote}
                      onChange={e => setNewNote(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleAddNote();
                        }
                      }}
                      placeholder="Add a note…"
                      className="flex-1 px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 focus:ring-primary-500/20 outline-none transition-all"
                    />
                    <button
                      onClick={handleAddNote}
                      disabled={loading || !newNote.trim()}
                      className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border-light">
          {showDeleteConfirm ? (
            <div className="bg-error-light/20 p-4 rounded-lg">
              <p className="text-error-dark mb-4">
                Are you sure you want to delete this task? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-text-secondary hover:text-text-primary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="px-4 py-2 bg-error-DEFAULT text-white rounded-lg hover:bg-error-dark disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <div>
                {canEdit && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center gap-2 text-error-DEFAULT hover:text-error-dark"
                  >
                    <Trash2 className="w-5 h-5" />
                    Delete
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-text-secondary hover:text-text-primary"
                >
                  Close
                </button>
                {canEdit && (
                  isEditing ? (
                    <button
                      onClick={handleSave}
                      disabled={loading}
                      className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
                    >
                      {loading ? 'Saving…' : 'Save Changes'}
                    </button>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex items-center gap-2 px-4 py-2 text-primary-500 hover:text-primary-600"
                    >
                      <Edit2 className="w-5 h-5" />
                      Edit
                    </button>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
